import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import ARIcon from 'src/shared/material_icons/action/view_in_ar.svg';
import CameraIcon from 'src/shared/material_icons/image/photo_camera.svg';
import SettingsIcon from 'src/shared/material_icons/action/settings.svg';
import LightsIcon from 'src/shared/material_icons/action/lightbulb.svg';
import ObjectIcon from 'src/shared/material_icons/action/group_work.svg';
import MaterialsIcon from 'src/shared/material_icons/image/texture.svg';
import { T } from '../../../../../shared/helpers';
import { useSubscriptionData } from '../../../../../shared/hooks/useSubscriptionData';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../widgets/notificationToaster';
import { VtoIcon } from '../../../../widgets/svg';
import { useProductConfigurationState } from '../../state';
import HeaderToolbar from '../configurator-components/headerToolbar';
import LeftVerticalToolbar, { StepsTools } from '../configurator-components/leftVerticalToolbar';
import AREditor from '../configurator-components/scene-editors/arEditor';
import CameraEditor from '../configurator-components/scene-editors/cameraEditor';
import LightsEditor from '../configurator-components/scene-editors/lightsEditor';
import MaterialsEditor from '../configurator-components/scene-editors/materialsEditor';
import ObjectEditor from '../configurator-components/scene-editors/objectEditor';
import SettingsEditor from '../configurator-components/scene-editors/settingsEditor';
import VTOEditor from '../configurator-components/scene-editors/vtoEditor';
import { TreeHandle } from '../configurator-components/TreeComponent';

// Relay generated types
import useSaveProductAndScene from 'src/components/widgets/preview3D/hooks/useSaveProduct';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import OverlayLoading from '../../../../widgets/overlayLoading';
import { Load3DSceneEditor } from '../../../../widgets/preview3D';
import SceneEditorView from '../../../../widgets/preview3D/sceneEditorView';
import { SceneEditorToolbar } from '../configurator-components/scene-editors/toolbar';

interface MeshTreeViewItem {
    id: string;
    name: string;
}

enum Mode {
    Meshes = 'meshes',
    Materials = 'materials',
    Lights = 'lights',
    Camera = 'camera'
}

interface SceneEditorPageState {
    isEnvironmentTextureUploading: boolean;
    isSkyboxTextureUploading: boolean;
    zkModel: any;
    zkScene: any;
    meshes: any[];
    materials: any[];
    lights: any[];
    cameraLocations: any[];
    tasks: any[];
    selectedMeshes: any[];
    selectedMaterials: any[];
    selectedLights: any[];
    selectedCameraLocations: any[];
    filters: {
        meshes: string;
        lights: string;
        materials: string;
    };
    mode: Mode;
    propertiesPanelOpen: boolean;
    loadingPercentage: number;
    texturesLoading: any[];
    showPreview: boolean;
    explodeMode: boolean;
    hasVTOReferenceModels: boolean;
    selectedVTOType: any;
    tryOnSettingsComponent: React.RefObject<any>;
    isAdmin: boolean;
    importDropdownMenuIsOpened: boolean;
    isUploadingDamMaterial: boolean;
    providersMapping: {
        list: Record<string, any>;
    };
    uploadedQueue: any[];
    groupMaterialsDisplayOrder: Map<string, number>;
}

const Container = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: rgb(246, 248, 249);
    z-index: 30;
    width: 100%;

    & > * {
        width: 100%;
    }
`;

const MainContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
`;

const MainContentLayout = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    height: 100%;
    max-height: 100%;
    background: #f6f8f9;
`;

const PreviewContainer = styled.div`
    flex: 1;
    padding: 20px 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
`;

const LeftVerticalToolbarContainer = styled.div<{ $hidden?: boolean }>`
    padding: 0 12px 12px;
    overflow-y: auto;
    width: calc(54px + 350px);
    ${(props) =>
        props.$hidden &&
        css`
            display: none;
        `}
    position: relative;
`;

const SceneEditor: React.FC = () => {
    // Hook per ottenere i dati della subscription con additionalFeatures
    const { subscription } = useSubscriptionData();
    const additionalFeatures = subscription?.plan?.additionalFeatures;

    // Refs per mantenere le referenze come nella classe
    const treeRef = useRef<TreeHandle>(null);
    const meshesScrollbarsRef = useRef<any>(null);
    const materialsScrollbarsRef = useRef<any>(null);

    // Hook per il salvataggio del prodotto e scena
    const [saveProductAndScene, isSaving] = useSaveProductAndScene();

    // Variabili di istanza convertite in useRef per persistenza
    const lastTasksLengthRef = useRef<number>(0);
    const treeItemsRef = useRef<MeshTreeViewItem[]>([]);
    const creatingGroupParentRef = useRef<any | null>(null);
    const sidebarWidthRef = useRef<number>(0);
    const sceneChangedRef = useRef<boolean>(false);
    const currentTaskRef = useRef<any | null>(null);

    // Stato iniziale convertito da this.state
    const [state, setState] = useState<SceneEditorPageState>({
        isEnvironmentTextureUploading: false,
        isSkyboxTextureUploading: false,
        zkModel: null,
        zkScene: undefined,
        meshes: [],
        materials: [],
        lights: [],
        cameraLocations: [],
        tasks: [],
        selectedMeshes: [],
        selectedMaterials: [],
        selectedLights: [],
        selectedCameraLocations: [],
        filters: { meshes: '', lights: '', materials: '' },
        mode: Mode.Meshes,
        propertiesPanelOpen: false,
        loadingPercentage: 0,
        texturesLoading: [],
        showPreview: false,
        explodeMode: false,
        hasVTOReferenceModels: false,
        selectedVTOType: undefined,
        tryOnSettingsComponent: createRef(),
        isAdmin: localStorage.getItem('is_admin') ? true : false,
        importDropdownMenuIsOpened: false,
        isUploadingDamMaterial: false,
        providersMapping: { list: {} },
        uploadedQueue: [],
        groupMaterialsDisplayOrder: new Map<string, number>()
    });

    // Stato per overlay loading (convertito da proprietà di classe)
    const { dispatch, state: productState } = useProductConfigurationState();
    const [zkProduct, zkScene, sceneEditor] = usePreview3DStore((x) => [x.zkProduct, x.zkScene, x.sceneEditor]);
    const [isSceneEditorReady, setIsSceneEditorReady] = useState(false);

    // Ref per la funzione di refresh della visibilità delle mesh
    const refreshMeshVisibilityRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        console.log('SceneEditor mounted');
        if (sceneEditor && zkScene) {
            console.log('🔍 SceneEditor disponible, caricamento dati...');
            const meshes = sceneEditor.getSceneMeshes();
            const materials = sceneEditor.getAllMaterials();
            const lights = sceneEditor.getAllLights();

            // AGGIUNTO: Carica le camera locations dal zkScene
            const cameraLocations = zkScene.get('cameraLocations')?.models || [];
            console.log('🎨 Materials loaded from scene:', materials);
            console.log('💡 Lights loaded from scene:', lights);
            console.log('📷 Camera locations loaded from scene:', cameraLocations);
            console.log('� Number of camera locations:', cameraLocations?.length || 0);

            handleMeshesUpdated(meshes);
            handleMaterialsUpdated(materials);
            handleCameraLocationsUpdated(cameraLocations);
            setIsSceneEditorReady(true);

            // Refresh mesh visibility dopo il caricamento iniziale
            setTimeout(() => {
                if (refreshMeshVisibilityRef.current) {
                    console.log('🔄 Triggering mesh visibility refresh after scene load');
                    refreshMeshVisibilityRef.current();
                }
            }, 100);
        } else {
            console.log('⚠️ SceneEditor o zkScene non ancora disponibile');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneEditor, zkScene]);

    // Event handlers stabilizzati con useCallback
    const handleMeshesUpdated = useCallback((meshes: any[]) => {
        console.log('Meshes updated:', meshes);
        if (state.meshes.length > 0) {
            sceneChangedRef.current = true;
        }

        setState((prevState) => ({
            ...prevState,
            meshes: (meshes ?? [])
                .filter((mesh) => {
                    return mesh.name.toLowerCase() !== 'material_mesh';
                })
                .sort((a: any, b: any) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    return 0;
                })
        }));
    }, []);

    const handleMaterialsUpdated = useCallback((materials: any[]) => {
        console.log('📥 handleMaterialsUpdated chiamato con materials:', materials);

        // Il problema: a volte riceviamo direttamente i materiali, altre volte un gruppo che li contiene
        let actualMaterials = materials;

        // Se il primo elemento ha una proprietà 'materials', allora è un gruppo
        if (materials && materials.length > 0 && materials[0] && materials[0].materials) {
            console.log(' Detected group format, extracting materials from group');
            actualMaterials = materials[0].materials;
        }

        const filteredMaterials = (actualMaterials ?? [])
            .filter((material) => {
                return material.name && material.name.toLowerCase() !== 'default_material';
            })
            .sort((a: any, b: any) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
            });

        setState((prevState) => ({
            ...prevState,
            materials: filteredMaterials
        }));
    }, []);

    // Aggiorna handleLightsUpdated per essere più completo
    const handleLightsUpdated = useCallback((lights: any[]) => {
        console.log('💡 handleLightsUpdated chiamato con lights:', lights);

        const filteredLights = (lights ?? [])
            .filter((light) => {
                // Filtra eventuali luci di sistema o default se necessario
                return light.name && light.name.toLowerCase() !== 'default_light';
            })
            .sort((a: any, b: any) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
            });

        if (state.lights.length > 0) {
            sceneChangedRef.current = true;
        }

        setState((prevState) => ({
            ...prevState,
            lights: filteredLights
        }));
    }, []);

    const handleCameraLocationsUpdated = useCallback((cameraLocations: any[]) => {
        setState((s) => ({ ...s, cameraLocations }));
    }, []);

    // Callback per registrare la funzione di refresh delle mesh
    const handleRefreshMeshVisibility = useCallback((refreshFn: () => void) => {
        refreshMeshVisibilityRef.current = refreshFn;
    }, []);

    const handleSceneLoaded = useCallback((scene: BABYLON.Scene) => {
        console.log('Scene loaded:', scene);
    }, []);

    const handleBeforeSceneLoaded = useCallback(async () => {
        console.log('Before scene loaded');
    }, []);

    const handleSceneTasksCheck = useCallback((...args: any[]) => {
        // If the first argument is the tasks array, update state accordingly
        const tasks = Array.isArray(args[0]) ? args[0] : args;
        setState((s) => ({ ...s, tasks }));
    }, []);

    const handleMeshUpdated = useCallback((updatedMesh: any) => {
        console.log('Mesh updated:', updatedMesh);
        setState((prevState) => ({
            ...prevState,
            meshes: prevState.meshes.map((mesh) => (mesh.uniqueId === updatedMesh.uniqueId ? updatedMesh : mesh))
        }));
    }, []);

    const notifySceneEditorUpdates = useCallback(() => {
        if (!sceneEditor) return;

        sceneEditor.render();
        sceneEditor.updateBoundingBox();

        console.log('SceneEditor updates notified');
    }, [sceneEditor]);

    // Funzione per il salvataggio usando l'hook
    const handleSave = useCallback(async () => {
        try {
            console.log('Avvio salvataggio prodotto e scena...');

            const productInfo = {
                name: productState.product.name,
                code: productState.product.code,
                environment: 2
            };

            console.log('🔍 Product info from ProductConfigurationState:', productInfo);

            // ✅ NUOVO: Validazione AR PRIMA della notificazione di saving
            if (sceneEditor) {
                const { validateARBeforeSave } = await import('src/components/widgets/preview3D/hooks/useSaveProduct');
                await validateARBeforeSave(sceneEditor);
            }

            // Se il VTO è attivo, gestisci le impostazioni VTO prima del salvataggio
            if (sceneEditor && zkScene) {
                // Always hide all references on save so they are not visible and not
                // downloaded at start of customizer/DAM
                sceneEditor.setVTOReferenceVisible(false);

                // Verifica se il VTO è abilitato e applica le impostazioni
                const tryOnSettings = sceneEditor.getTryOnSettings();
                if (tryOnSettings && zkScene.getTryOnEnabled()) {
                    console.log('VTO è attivo, applicando le impostazioni VTO...');
                    // Applica le impostazioni VTO correnti alla scena
                    sceneEditor.setTryOnTypeSettings(tryOnSettings);
                }

                // Imposta il nome della scena basato sul nome del prodotto
                zkScene.set('name', productInfo.name + ' Scene');
            }

            // Mostra notifica di progresso
            const notificationId = showInfoNotification({
                message: T._('Saving scene...', 'Admin')
            });

            await saveProductAndScene({
                onProgress: (progress) => {
                    console.log('Progresso salvataggio:', progress);
                },
                fromSceneEditor: true,
                // ✅ NUOVO: Passa i dati corretti del prodotto
                productInfo: productInfo
            });

            // Rimuovi notifica di progresso e mostra successo
            removeNotification(notificationId);
            showSuccessNotification({
                message: T._('Scene saved successfully', 'Admin')
            });

            console.log('Salvataggio completato con successo');
        } catch (error) {
            console.error('Errore durante il salvataggio:', error);

            // Mostra notifica di errore
            showErrorNotification({
                message: T._('Failed to save the scene', 'Admin')
            });
        }
    }, [saveProductAndScene, sceneEditor, zkScene, productState.product]);

    // Configurazione tools come nell'originale
    const sceneEditorTools: StepsTools[] = useMemo(
        () => [
            {
                tool: T._('Objects', 'Admin'),
                icon: <ObjectIcon />,
                tooltip: T._('Objects', 'Admin'),
                onClick: () => {
                    console.log('Objects tab clicked');
                },
                component: (props: any) => (
                    <ObjectEditor
                        onMeshUpdate={handleMeshUpdated}
                        sceneEditor={sceneEditor}
                        notifySceneEditorUpdates={notifySceneEditorUpdates}
                        onMeshesUpdated={handleMeshesUpdated}
                        onRefreshMeshVisibility={handleRefreshMeshVisibility}
                        {...props}
                        treeRef={treeRef}
                        additionalFeatures={additionalFeatures}
                    />
                )
            },
            {
                tool: T._('Materials', 'Admin'),
                icon: <MaterialsIcon />,
                tooltip: T._('Materials', 'Admin'),
                onClick: () => {
                    console.log('Materials tab clicked');
                },
                component: (props: any) => (
                    <MaterialsEditor
                        {...props}
                        materials={state.materials}
                        selectedMaterials={state.selectedMaterials}
                        sceneEditor={sceneEditor}
                        zkScene={zkScene}
                        onMaterialsUpdated={handleMaterialsUpdated}
                        treeRef={treeRef}
                        additionalFeatures={additionalFeatures}
                    />
                )
            },
            {
                tool: T._('Lights', 'Admin'),
                icon: <LightsIcon />,
                tooltip: T._('Lights', 'Admin'),
                onClick: () => {
                    console.log('Lights tab clicked');
                },
                component: (props: any) => (
                    <LightsEditor
                        {...props}
                        lights={state.lights}
                        selectedLights={state.selectedLights}
                        sceneEditor={sceneEditor}
                        onLightsUpdated={handleLightsUpdated}
                        treeRef={treeRef}
                        additionalFeatures={additionalFeatures}
                    />
                )
            },
            {
                tool: T._('Camera', 'Admin'),
                icon: <CameraIcon />,
                tooltip: T._('Camera', 'Admin'),
                onClick: () => {
                    console.log('Camera tab clicked');
                },
                component: (props: any) => (
                    <CameraEditor
                        {...props}
                        cameraLocations={state.cameraLocations}
                        selectedCameraLocations={state.selectedCameraLocations}
                        sceneEditor={sceneEditor}
                        zkScene={zkScene}
                        onCameraLocationsUpdated={handleCameraLocationsUpdated}
                        treeRef={treeRef}
                        additionalFeatures={additionalFeatures}
                    />
                )
            },
            {
                tool: T._('Augmented Reality', 'Admin'),
                icon: <ARIcon />,
                tooltip: T._('Augmented Reality', 'Admin'),
                onClick: () => {
                    console.log('AR tab clicked');
                },
                component: (props: any) => (
                    <AREditor {...props} sceneEditor={sceneEditor} zkScene={zkScene} treeRef={treeRef} additionalFeatures={additionalFeatures} />
                )
            },
            {
                tool: T._('Virtual Try-On', 'Admin'),
                icon: <VtoIcon />,
                tooltip: T._('Virtual Try-On', 'Admin'),
                onClick: () => {
                    console.log('VTO tab clicked');
                },
                component: (props: any) => <VTOEditor {...props} additionalFeatures={additionalFeatures} />
            },
            {
                tool: T._('Settings', 'Admin'),
                icon: <SettingsIcon />,
                tooltip: T._('Settings', 'Admin'),
                onClick: () => {
                    console.log('Settings tab clicked');
                },
                component: (props: any) => (
                    <SettingsEditor
                        {...props}
                        sceneEditor={sceneEditor}
                        zkScene={zkScene}
                        isAIEnabled={zkProduct?.get('isAiConfigurationEnabled')}
                        setAIEnabled={(enabled: boolean) => zkProduct?.set('isAiConfigurationEnabled', enabled)}
                        additionalFeatures={additionalFeatures}
                    />
                )
            }
        ],
        [
            sceneEditor,
            zkScene,
            handleMeshUpdated,
            notifySceneEditorUpdates,
            handleMeshesUpdated,
            handleMaterialsUpdated,
            state.materials,
            state.selectedMaterials,
            handleLightsUpdated,
            state.lights,
            state.selectedLights,
            handleCameraLocationsUpdated,
            state.cameraLocations,
            state.selectedCameraLocations
        ]
    );

    const handleMeshSelected = useCallback(
        (meshID: any) => {
            console.log('🎯 handleMeshSelected sceneEditor called with meshID:', meshID);
            console.log(
                '🎯 Available tools:',
                sceneEditorTools.map((t) => t.tool)
            );

            // Trova il tool Objects attualmente attivo - usa il nome tradotto
            const objectsToolComponent = sceneEditorTools.find((tool) => tool.tool === T._('Objects', 'Admin'));
            if (objectsToolComponent && treeRef.current) {
                console.log('🎯 Found Objects tool, calling selectAndOpenOverlay with meshID:', meshID);
                // Seleziona il nodo nell'albero e apri l'overlay
                treeRef.current.selectAndOpenOverlay?.(meshID);
            } else {
                console.warn('❌ Objects tool not found or treeRef not available:', {
                    objectsToolFound: !!objectsToolComponent,
                    treeRefAvailable: !!treeRef.current,
                    treeRefMethods: treeRef.current ? Object.keys(treeRef.current) : [],
                    availableTools: sceneEditorTools.map((t) => t.tool)
                });
            }
        },
        [sceneEditorTools]
    );

    const closeOverlay = useCallback(() => {
        console.log('closeOverlay called');
        if (treeRef && treeRef.current) {
            treeRef.current.closeOverlay();
        }
    }, []);

    return (
        <Load3DSceneEditor onProgress={() => null} fallback={<OverlayLoading forceShow />} damMode={false} isConfiguratorProduct={true} notLoadDesign={true}>
            <Container>
                <HeaderToolbar closeOverlay={closeOverlay} onSave={handleSave} isSaving={isSaving} />
                <MainContentContainer>
                    <MainContentLayout>
                        <LeftVerticalToolbarContainer $hidden={false}>
                            <LeftVerticalToolbar tools={sceneEditorTools} />
                        </LeftVerticalToolbarContainer>
                        <PreviewContainer>
                            {zkScene && zkProduct && sceneEditor && (
                                <>
                                    <SceneEditorView
                                        variantId={zkProduct.get('colors').at(0).get('colorID')}
                                        isHighlightActive={true}
                                        selectedMeshId={null}
                                        isSelectionMode={true}
                                        onSelectionChanged={handleMeshSelected}
                                        onMeshesUpdated={handleMeshesUpdated}
                                        onMaterialsUpdated={handleMaterialsUpdated}
                                        onLightsUpdated={handleLightsUpdated}
                                        onCameraLocationsUpdated={handleCameraLocationsUpdated}
                                        onSceneTasksCheck={handleSceneTasksCheck}
                                    />
                                    {isSceneEditorReady && <SceneEditorToolbar sceneEditor={sceneEditor} />}
                                </>
                            )}
                        </PreviewContainer>
                    </MainContentLayout>
                </MainContentContainer>
            </Container>
        </Load3DSceneEditor>
    );
};

export default SceneEditor;
